/**
 * Shared drag-and-drop behaviour for the visual menu editors (MenuGrid + MenuTabsEditor).
 *
 * Provides the common skeleton:
 *   - data:           draggingItemUuid, hoverTargetUuid, dragClientX/Y, lastHoverCell,
 *                     undoStack (LIFO of pre-drag snapshots, capped at MAX_UNDO_DEPTH)
 *   - listeners:      document-level mousemove/mouseup/keydown, registered in mounted,
 *                     removed in beforeDestroy
 *   - clone DOM:      _createDragClone / _moveDragClone / _destroyDragClone (transform-based, GPU-friendly)
 *   - hit-testing:    getHoveredCellUuid(containerEl, clientX, clientY)
 *   - undo:           pushUndoSnapshot()/undoLastDrag(); Cmd/Ctrl+Z restores the last
 *                     drag when focus is INSIDE this editor's $el (prevents conflicts
 *                     with text inputs and multiple editors mounted simultaneously)
 *
 * Consumers must implement:
 *   - startDrag(event, item)         — called from cell drag-handle mousedown
 *   - getDragContainer()             — returns the DOM element containing data-uuid cells
 *   - getCellDimensions(item)        — returns { width, height } in px for the drag clone
 *   - getCellIconUrl(item)           — returns the icon URL or null for the drag clone
 *   - itemLabel(item)                — returns the label text for the drag clone
 *   - handleHoverChanged(uuid)       — (optional) invoked when the hovered cell uuid
 *                                       changes (may be null). The consumer resolves
 *                                       the item from `uuid` using its own model.
 *   - handleEdgeScroll(clientY)      — (optional) invoked on every mousemove during drag
 *   - commitReorder()                — invoked on mouseup; consumer mutates its model
 *                                       accordingly. Should call pushUndoSnapshot()
 *                                       AFTER the no-op guard but BEFORE the mutation.
 *   - getMenuSnapshot()              — (optional, required for undo) returns a deep
 *                                       clone of the consumer's items tree
 *   - restoreMenuSnapshot(snapshot)  — (optional, required for undo) replaces the
 *                                       items tree with the snapshot in a reactive way
 *
 * All consumer callbacks may be no-ops; the mixin provides only the lifecycle and DOM plumbing.
 *
 * Naming convention
 * ─────────────────
 * - `startDrag`, `onDocMouseMove`, `onDocMouseUp`, `getHoveredCellUuid`:
 *   public hooks the consumer calls or overrides.
 * - `_createDragClone`, `_moveDragClone`, `_setDragCloneTransform`,
 *   `_destroyDragClone`: internal plumbing — the underscore prefix signals
 *   "don't call/override these from a consumer". They mutate the imperative
 *   `document.body` clone element directly.
 * - Non-reactive instance properties (`this._dragCloneEl`) keep the underscore
 *   to distinguish them from Vue's reactive `data()` fields.
 */

import { DRAG_CLONE_Z_INDEX } from '@/modules/Apps/Components/configurator/_shared/phoneDimensions'

// Max number of consecutive drags that can be undone. Older entries are dropped
// FIFO so the stack stays bounded for long editing sessions.
const MAX_UNDO_DEPTH = 20

// Module-level pointer to the editor that performed the most recent drag.
// Cmd/Ctrl+Z is scoped to this instance so multiple mounted editors don't all
// consume the same keystroke. Cleared in beforeDestroy.
//
// We can't scope by DOM focus because the drag handle uses `@mousedown.prevent`
// (intentional, to suppress text selection during drag) which prevents the
// button from receiving focus — so after a drop activeElement is `<body>` and
// a focus-based check would silently fail.
let _activeUndoInstance = null

export default {
    data() {
        return {
            draggingItemUuid: null,
            hoverTargetUuid:  null,
            dragClientX: 0,
            dragClientY: 0,
            lastHoverCell: null,
            undoStack: [],
        }
    },

    mounted() {
        this._onDocMouseMove = (e) => this.onDocMouseMove(e)
        this._onDocMouseUp   = (e) => this.onDocMouseUp(e)
        this._onDocKeydown   = (e) => this._handleUndoKeydown(e)
        document.addEventListener('mousemove', this._onDocMouseMove)
        document.addEventListener('mouseup',   this._onDocMouseUp)
        document.addEventListener('keydown',   this._onDocKeydown)
    },

    beforeDestroy() {
        document.removeEventListener('mousemove', this._onDocMouseMove)
        document.removeEventListener('mouseup',   this._onDocMouseUp)
        document.removeEventListener('keydown',   this._onDocKeydown)
        this._destroyDragClone()
        if (_activeUndoInstance === this) _activeUndoInstance = null
        if (this.draggingItemUuid) {
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
        }
    },

    methods: {
        startDrag(event, item) {
            this.draggingItemUuid = item.uuid
            this.dragClientX = event.clientX
            this.dragClientY = event.clientY
            this.hoverTargetUuid = null
            this.lastHoverCell = null
            document.body.style.cursor = 'grabbing'
            document.body.style.userSelect = 'none'
            this._createDragClone(item, event.clientX, event.clientY)
        },

        onDocMouseMove(event) {
            if (!this.draggingItemUuid) return
            this.dragClientX = event.clientX
            this.dragClientY = event.clientY
            this._moveDragClone(event.clientX, event.clientY)

            // Consumer hook — typically calls PhoneScreen.applyEdgeScroll
            if (this.handleEdgeScroll) this.handleEdgeScroll(event.clientY)

            // Consumer hook — runs BEFORE the cell-hover update so it can short-circuit
            // (e.g., when the cursor is over the back-arrow region).
            if (this.handlePreHoverChanged && this.handlePreHoverChanged(event) === true) return

            const hoveredUuid = this.getHoveredCellUuid(event.clientX, event.clientY)

            // Anti-flicker: ignore phantom hits on the dragging item itself.
            // After a preview re-render the dragging item slides into the target's old
            // position, so the live query returns its uuid even though the cursor
            // hasn't moved. Treat as a non-event (no state change, no timer reset).
            if (hoveredUuid === this.draggingItemUuid) return

            if (hoveredUuid === this.lastHoverCell) return
            this.lastHoverCell = hoveredUuid

            this.hoverTargetUuid = hoveredUuid || null

            // Consumer hook — receives the new hovered uuid (may be null).
            // The consumer resolves the item from its own model (we don't try to
            // be helpful here because the lookup scope is consumer-specific).
            if (this.handleHoverChanged) this.handleHoverChanged(hoveredUuid)
        },

        onDocMouseUp() {
            if (!this.draggingItemUuid) return

            if (this.commitReorder) this.commitReorder()

            this._destroyDragClone()
            this.draggingItemUuid = null
            this.hoverTargetUuid = null
            this.lastHoverCell = null
            document.body.style.cursor = ''
            document.body.style.userSelect = ''

            // The browser synthesises a `click` on the common ancestor of the
            // mousedown and mouseup targets. After a drag, that click is
            // semantically wrong — it would re-trigger cell click handlers
            // (e.g., entering a submenu when the user drops onto one). Swallow
            // it once via a capture-phase listener that auto-removes.
            this._swallowNextClick()
        },

        _swallowNextClick() {
            const swallow = (e) => {
                e.stopPropagation()
                e.preventDefault()
                document.removeEventListener('click', swallow, true)
            }
            document.addEventListener('click', swallow, true)
            // Safety: if no click fires (e.g., release over inert area), drop
            // the listener on the next tick so it can't intercept a later one.
            setTimeout(() => document.removeEventListener('click', swallow, true), 0)
        },

        // ---- Undo stack ----

        /**
         * Push a snapshot onto the undo stack. By default the consumer captures
         * the current items tree via `getMenuSnapshot()`; callers can pass a
         * pre-built snapshot (e.g., from the parent component, to capture a
         * pre-mutation state from outside the editor).
         *
         * The stack is capped at MAX_UNDO_DEPTH — older entries drop FIFO.
         * Also marks this instance as the most-recent actor; Cmd/Ctrl+Z routes
         * to whichever editor pushed last (see _handleUndoKeydown).
         *
         * Returns the snapshot that was pushed (or null), so callers can keep a
         * reference for later removal via `removeUndoSnapshot`.
         */
        pushUndoSnapshot(externalSnapshot) {
            let snapshot = externalSnapshot
            if (snapshot === undefined && this.getMenuSnapshot) {
                snapshot = this.getMenuSnapshot()
            }
            if (!snapshot) return null
            this.undoStack.push(snapshot)
            if (this.undoStack.length > MAX_UNDO_DEPTH) this.undoStack.shift()
            _activeUndoInstance = this
            return snapshot
        },

        /**
         * Remove a previously-pushed snapshot from the stack by reference.
         * Used when a tentative action is rolled back (e.g., an abandoned
         * "Create item" placeholder that never got configured).
         */
        removeUndoSnapshot(snapshotRef) {
            if (!snapshotRef) return false
            const idx = this.undoStack.indexOf(snapshotRef)
            if (idx === -1) return false
            this.undoStack.splice(idx, 1)
            return true
        },

        /**
         * Pop the most recent snapshot and ask the consumer to restore it.
         * Returns true if an undo happened, false if the stack was empty or
         * the consumer can't restore.
         */
        undoLastDrag() {
            if (this.undoStack.length === 0) return false
            if (!this.restoreMenuSnapshot) return false
            const snapshot = this.undoStack.pop()
            this.restoreMenuSnapshot(snapshot)
            return true
        },

        _handleUndoKeydown(event) {
            // Cmd+Z (macOS) or Ctrl+Z (others). Shift+Cmd+Z is the redo
            // convention — ignore so the platform can use it elsewhere.
            const isUndo = (event.metaKey || event.ctrlKey) && event.key === 'z' && !event.shiftKey
            if (!isUndo) return
            // Defer to the browser when focus is in an editable text element.
            const active = document.activeElement
            if (active) {
                const tag = active.tagName
                if (tag === 'INPUT' || tag === 'TEXTAREA' || active.isContentEditable) return
            }
            // Scope: only the editor that performed the most recent drag handles
            // Cmd+Z. Avoids two mounted editors both consuming the keystroke and
            // works regardless of focus state (the drag handle uses .prevent so
            // focus never lands inside the editor after a drop).
            if (_activeUndoInstance !== this) return
            if (this.undoStack.length === 0) return
            event.preventDefault()
            this.undoLastDrag()
        },

        // ---- DOM hit-testing ----

        getHoveredCellUuid(clientX, clientY) {
            const container = this.getDragContainer ? this.getDragContainer() : null
            if (!container) return null
            const cells = container.querySelectorAll('[data-uuid]')
            for (let i = 0; i < cells.length; i++) {
                const rect = cells[i].getBoundingClientRect()
                if (clientX >= rect.left && clientX <= rect.right &&
                    clientY >= rect.top  && clientY <= rect.bottom) {
                    return cells[i].getAttribute('data-uuid')
                }
            }
            return null
        },

        // ---- Drag clone (imperative DOM on document.body) ----
        //
        // Mounted on document.body so position:fixed works regardless of CSS
        // transforms on ancestors. Uses transform: translate3d for movement
        // (GPU-composited, no layout pass per mousemove).
        //
        // Preferred path: deep-clone the source cell's actual DOM so the floating
        // preview matches whatever the user is dragging (size, background image,
        // text overlay, badges, fonts). Falls back to a generic purple box when the
        // source isn't in the DOM (e.g., in unit tests without rendered cells).

        _createDragClone(item, clientX, clientY) {
            this._destroyDragClone()
            const source = this._findSourceCell(item.uuid)
            const el = source ? this._buildCloneFromSource(source) : this._buildFallbackClone(item)
            this._applyCloneChrome(el, source, item)
            this._setDragCloneTransform(el, clientX, clientY)
            document.body.appendChild(el)
            this._dragCloneEl = el
        },

        _findSourceCell(uuid) {
            const container = this.getDragContainer ? this.getDragContainer() : null
            if (!container || !uuid) return null
            return container.querySelector(`[data-uuid="${uuid}"]`)
        },

        _buildCloneFromSource(source) {
            const clone = source.cloneNode(true)
            // Strip the hover-actions group (drag handle + edit + delete) from
            // the floating clone — it'd render via :hover from the source CSS
            // and visually pollute the drag preview.
            clone.querySelectorAll('.menu-grid-cell__hover-actions').forEach(n => n.remove())
            // Strip transient state classes — the clone is a snapshot at drag-start
            // and shouldn't pick up live editing/dragging/hover-hold/drop-target visuals.
            clone.classList.remove(
                'menu-grid-cell--dragging',
                'menu-grid-cell--editing',
                'menu-grid-cell--hover-hold',
                'menu-grid-cell--drop-target',
            )
            return clone
        },

        _buildFallbackClone(item) {
            const url   = this.getCellIconUrl ? this.getCellIconUrl(item) : null
            const label = this.itemLabel      ? this.itemLabel(item)      : ''
            const el = document.createElement('div')
            el.style.cssText = [
                'overflow:hidden',
                'display:flex',
                'align-items:flex-end',
                'justify-content:center',
                'background-color:rgba(99,102,241,0.65)',
                'border-radius:4px',
            ].join(';')
            if (url) {
                el.style.backgroundImage = `url(${url})`
                el.style.backgroundSize  = '100% 100%'
            }
            if (label) {
                const p = document.createElement('p')
                p.style.cssText = 'margin:0;padding:2px 4px;color:#fff;font-size:11px;text-align:center;text-shadow:0 1px 2px rgba(0,0,0,0.7);'
                p.textContent = label
                el.appendChild(p)
            }
            return el
        },

        _applyCloneChrome(el, source, item) {
            // Width/height: read from the rendered source for pixel-perfect match;
            // fall back to the consumer's hint otherwise.
            let width  = 60
            let height = 60
            if (source) {
                const rect = source.getBoundingClientRect()
                width = rect.width
                height = rect.height
            } else if (this.getCellDimensions) {
                const dims = this.getCellDimensions(item)
                width = dims.width
                height = dims.height
            }
            el.style.position      = 'fixed'
            el.style.pointerEvents = 'none'
            el.style.zIndex        = String(DRAG_CLONE_Z_INDEX)
            el.style.opacity       = '0.85'
            el.style.top           = '0'
            el.style.left          = '0'
            el.style.width         = `${width}px`
            el.style.height        = `${height}px`
            el.style.boxShadow     = '0 6px 20px rgba(0,0,0,0.35)'
            el.style.willChange    = 'transform'
            el.style.margin        = '0'
        },

        _moveDragClone(clientX, clientY) {
            if (this._dragCloneEl) this._setDragCloneTransform(this._dragCloneEl, clientX, clientY)
        },

        _setDragCloneTransform(el, clientX, clientY) {
            // -50% / -50% centres the clone on the cursor.
            // translate3d forces a separate compositor layer (GPU).
            el.style.transform = `translate3d(${clientX}px, ${clientY}px, 0) translate(-50%, -50%)`
        },

        _destroyDragClone() {
            if (this._dragCloneEl) {
                if (this._dragCloneEl.parentNode) {
                    this._dragCloneEl.parentNode.removeChild(this._dragCloneEl)
                }
                this._dragCloneEl = null
            }
        },
    },
}
